import { useEffect, useState, type ReactNode } from 'react';
import { brand, markBrandResolved, useBrandLogo, useBrandStatus, useSiteSettings } from '@/brands';
import { MaintenancePage } from '@/pages/Maintenance';
import { ForcePasswordGate } from './ForcePasswordGate';

const PREVIEW_KEY = 'brand-preview';

/**
 * Operator escape hatch: opening the site once with `?__preview=1` keeps the
 * real site visible for the rest of that tab's session, so an owner can check
 * their own brand while it is down for everyone else. Session-scoped on
 * purpose — closing the tab drops it, so it cannot be shared as a link that
 * quietly reopens a closed site.
 */
function usePreviewBypass(): boolean {
  const [on, setOn] = useState(() => {
    try {
      return sessionStorage.getItem(PREVIEW_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('__preview') !== '1') return;
    try {
      sessionStorage.setItem(PREVIEW_KEY, '1');
    } catch {
      /* private mode — the flag just won't survive a reload */
    }
    setOn(true);
    p.delete('__preview');
    const url =
      window.location.pathname +
      (p.toString() ? `?${p}` : '') +
      window.location.hash;
    window.history.replaceState({}, '', url);
  }, []);

  return on;
}

/** Brand-coloured splash held while GET /brand is still in flight. */
function BrandSplash() {
  const logo = useBrandLogo();
  return (
    <div className="site-gate-splash">
      <style>{SPLASH_CSS}</style>
      <img src={logo} alt={brand.name} />
      <span className="site-gate-spinner" />
    </div>
  );
}

/**
 * Decides whether players see the site at all.
 *
 * Wrapping the router (rather than adding a route) is deliberate: Maintenance
 * Mode has to cover every deep link, including ones a player already has open,
 * and the splash in front of it means a closed brand never flashes its real
 * site while the brand lookup is still resolving.
 */
export function SiteGate({ children }: { children: ReactNode }) {
  const status = useBrandStatus();
  const { maintenance, siteLive } = useSiteSettings();
  const preview = usePreviewBypass();

  // Failsafe: the brand fetch has no timeout of its own, so a request that
  // never settles would hold the splash forever. After 8s, carry on with the
  // defaults — a slow lookup must not be indistinguishable from a dead site.
  useEffect(() => {
    const t = setTimeout(markBrandResolved, 8000);
    return () => clearTimeout(t);
  }, []);

  if (status === 'loading') return <BrandSplash />;

  if (!preview) {
    if (maintenance) return <MaintenancePage mode="maintenance" />;
    if (!siteLive) return <MaintenancePage mode="comingSoon" />;
  }

  return (
    <>
      {children}
      <ForcePasswordGate />
    </>
  );
}

const SPLASH_CSS = `
.site-gate-splash{position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;
  align-items:center;justify-content:center;gap:22px;
  background:linear-gradient(160deg,#071a26 0%,#0a2233 45%,#050d14 100%);}
.site-gate-splash img{height:52px;max-width:200px;object-fit:contain;
  animation:site-gate-pulse 1.6s ease-in-out infinite;}
@keyframes site-gate-pulse{0%,100%{opacity:.55}50%{opacity:1}}
.site-gate-spinner{width:26px;height:26px;border-radius:50%;
  border:2.5px solid rgba(255,255,255,.16);
  border-top-color:var(--brand-primary,#ffb700);
  animation:site-gate-spin .8s linear infinite;}
@keyframes site-gate-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){
  .site-gate-splash img,.site-gate-spinner{animation:none}
}
`;
